import { DateTime } from "luxon";

export type FleetBookingAction = "check-in" | "check-out" | null;

/**
 * Determines which primary booking action (check-in / check-out) to offer for a fleet booking row.
 *
 * Semantics (see `docs/features/fleets/shared/fleet-booking-actions.md` for full truth table):
 * - **Check-in**: first action at booking start — shown when start date is today in `timezone`
 *   and booking has not yet ended (`end >= now`), while the trip is not yet marked as checked in.
 * - **Check-out**: closing action during the active period — shown when `isCheckedIn === true`
 *   and booking end is still in the future or today (`end >= now`).
 * - **Cancel** is not returned by this function; it is handled separately in `renderFleetEventActions`.
 *   Admin can cancel a booking anytime until the end of the end date (end of day via `isCancelAllowed`).
 */
export const getBookingAction = (
  startTime: string,
  endTime: string,
  timezone: string,
  isCheckedIn?: boolean,
  isCheckedOut?: boolean
): FleetBookingAction => {
  const now = DateTime.now().setZone(timezone);
  const start = DateTime.fromISO(startTime, { zone: "utc" }).setZone(timezone);
  const end = DateTime.fromISO(endTime, { zone: "utc" }).setZone(timezone);

  const isStartToday = start.toISODate() === now.toISODate();

  // If the trip has been completed (check-in/return done), return null
  if (isCheckedOut) return null;

  // Check-out: only after check-in, and booking is still active (end >= now)
  if (isCheckedIn && end >= now) return "check-out";

  // Check-in: allowed on booking start date (today), regardless of start time, while not checked in
  if (!isCheckedIn && isStartToday && end >= now) return "check-in";

  return null;
};

/** True when we are still on or before the end date (cancel allowed until end of day of the end date). */
export const isCancelAllowed = (endTime: string, timezone: string): boolean => {
  const now = DateTime.now().setZone(timezone);
  const end = DateTime.fromISO(endTime, { zone: "utc" }).setZone(timezone);
  const endOfEndDay = end.endOf("day");
  return endOfEndDay > now;
};

/** True while the booked visit slot has not ended yet (`end >= now` in booking timezone). */
export const isVisitSlotActive = (endTime: string, timezone: string): boolean => {
  const now = DateTime.now().setZone(timezone);
  const end = DateTime.fromISO(endTime, { zone: "utc" }).setZone(timezone);
  return end >= now;
};
